const PITCH_CLASSES = Object.freeze({
  C: 0, 'C#': 1, DB: 1, D: 2, 'D#': 3, EB: 3, E: 4,
  F: 5, 'F#': 6, GB: 6, G: 7, 'G#': 8, AB: 8, A: 9,
  'A#': 10, BB: 10, B: 11
});

const clampMidi = (value) => Math.max(0, Math.min(127, Math.round(value)));

export const rootNoteFromFilename = (filename, fallback = 72) => {
  const name = String(filename || '').replace(/\.[^.]+$/, '');
  const matches = [...name.matchAll(/(?:\[|\()([A-G])([#b]?)(-?\d+)?(?:\s+oneshot)?(?:\]|\))/gi)];
  const match = matches.at(-1);
  if (!match) return clampMidi(fallback);
  const pitchClass = PITCH_CLASSES[`${match[1].toUpperCase()}${match[2].toUpperCase()}`];
  if (!Number.isFinite(pitchClass)) return clampMidi(fallback);
  // CookUp's instrument/piano-roll convention anchors unnumbered one-shots at
  // C5 (MIDI 72), matching the sequencer's imported-pattern basis.
  if (match[3] == null) return clampMidi(72 + pitchClass);
  return clampMidi((Number(match[3]) + 1) * 12 + pitchClass);
};

export const filenameHasExplicitOctave = (filename) => (
  /(?:\[|\()[A-G](?:#|b)?-?\d+(?:\]|\))/i.test(String(filename || ''))
);

export const rootNoteFromAudioBuffer = (buffer, fallback = 72) => {
  if (!buffer?.getChannelData || !buffer.length) return clampMidi(fallback);
  const sourceRate = Number(buffer.sampleRate) || 44100;
  const stride = Math.max(1, Math.round(sourceRate / 12000));
  const analysisRate = sourceRate / stride;
  const start = Math.min(Math.max(0, buffer.length - 2), Math.floor(sourceRate * 0.08));
  const available = Math.floor((buffer.length - start) / stride);
  const size = Math.min(4096, available);
  if (size < 256) return clampMidi(fallback);
  const channels = Math.max(1, Number(buffer.numberOfChannels) || 1);
  const channelData = Array.from({ length: channels }, (_, channel) => buffer.getChannelData(channel));
  const samples = new Float32Array(size);
  for (let index = 0; index < size; index += 1) {
    const frame = start + index * stride;
    let sum = 0;
    for (let channel = 0; channel < channels; channel += 1) sum += channelData[channel][frame] || 0;
    samples[index] = sum / channels;
  }
  const minLag = Math.max(2, Math.floor(analysisRate / 1400));
  const maxLag = Math.min(Math.floor(size / 2), Math.floor(analysisRate / 38));
  const difference = new Float64Array(maxLag + 1);
  for (let lag = 1; lag <= maxLag; lag += 1) {
    let total = 0;
    for (let index = 0; index < size - lag; index += 1) {
      const delta = samples[index] - samples[index + lag];
      total += delta * delta;
    }
    difference[lag] = total;
  }
  let running = 0;
  let bestLag = minLag;
  let bestValue = Infinity;
  const normalized = new Float64Array(maxLag + 1);
  for (let lag = 1; lag <= maxLag; lag += 1) {
    running += difference[lag];
    normalized[lag] = running > 1e-12 ? difference[lag] * lag / running : 1;
    if (lag >= minLag && normalized[lag] < bestValue) {
      bestValue = normalized[lag];
      bestLag = lag;
    }
  }
  for (let lag = minLag; lag < maxLag; lag += 1) {
    if (normalized[lag] < 0.16 && normalized[lag] <= normalized[lag + 1]) {
      bestLag = lag;
      break;
    }
  }
  if (!Number.isFinite(bestValue) || bestValue > 0.55) return clampMidi(fallback);
  const frequency = analysisRate / bestLag;
  if (!Number.isFinite(frequency) || frequency < 38 || frequency > 1400) return clampMidi(fallback);
  return clampMidi(69 + 12 * Math.log2(frequency / 440));
};

export const buildSampleZones = (sources, options = {}) => {
  const entries = (Array.from(sources || []))
    .map((source, index) => ({
      source,
      index,
      rootNote: Number.isFinite(Number(source?.rootNote))
        ? clampMidi(source.rootNote)
        : rootNoteFromFilename(source?.name, options.fallbackRootNote ?? 72)
    }))
    .sort((a, b) => a.rootNote - b.rootNote || a.index - b.index);
  const roots = [...new Set(entries.map((entry) => entry.rootNote))];
  const rangeByRoot = new Map(roots.map((root, index) => {
    const previous = roots[index - 1];
    const next = roots[index + 1];
    const loNote = previous == null ? 0 : Math.floor((previous + root) / 2) + 1;
    const hiNote = next == null ? 127 : Math.floor((root + next) / 2);
    return [root, { loNote: clampMidi(loNote), hiNote: clampMidi(hiNote) }];
  }));
  const roundRobinByRoot = new Map();
  return entries.map(({ source, index, rootNote }) => {
    const roundRobin = roundRobinByRoot.get(rootNote) || 0;
    roundRobinByRoot.set(rootNote, roundRobin + 1);
    const range = rangeByRoot.get(rootNote);
    return {
      id: String(source?.id || `sample-${index + 1}`),
      bufferId: String(source?.bufferId || source?.id || `sample-${index + 1}`),
      url: source?.url || '',
      name: String(source?.name || `Sample ${index + 1}`),
      rootNote,
      loNote: range.loNote,
      hiNote: range.hiNote,
      loVelocity: 0,
      hiVelocity: 1,
      gain: 1,
      pan: 0,
      tune: 0,
      start: 0,
      end: null,
      loopEnabled: false,
      loopStart: 0,
      loopEnd: 0,
      loopCrossfade: 0.02,
      trigger: 'attack',
      roundRobin
    };
  });
};
